/**
 * L'ENTRÉE « SURVEILLANCE » DE LA BARRE DU HAUT.
 * ============================================================================
 *
 * Le cœur n'écrit qu'une ligne (`core/layouts/HeaderNavigation.vue`) ; la
 * décision est ici. L'entrée existe quand la garde serveur a ouvert le module
 * POUR LA SESSION COURANTE (la personne et l'entreprise) et que l'entreprise a l'offre Energy Manager.
 * Elle n'est jamais désactivée : un lecteur voit l'état et acquitte (T5 §A.1,
 * matrice geste 1). L'entrée « Alarmes » de la V1 ne change pas.
 */
import { getActivePinia } from "pinia"
import { useAuthStore } from "@core/stores/auth.store"
import { useOuvertureStore } from "@modules/alarmes-v2/stores/ouverture.store"
import type { AlarmesV2Surveillance } from "@modules/alarmes-v2/types/alarmes-v2"

/** Une entrée de premier niveau de la barre du haut. */
export interface EntreeMenuSurveillance {
    label: string
    icon: string
    route: { path: string }
}

/** Le chemin de la liste des surveillances. */
export const CHEMIN_SURVEILLANCES = "/surveillances"

/** Le chemin du catalogue des types d'alarme (lot F4). */
export const CHEMIN_CATALOGUE = "/surveillances/catalogue"

/** Le chemin de la page « Option Avancé et SMS » (écran 9, lot F5). */
export const CHEMIN_OPTION_AVANCE = "/surveillances/option-avance"

/**
 * L'entrée, ou `null`. À appeler dans un `computed` : elle lit les stores.
 * @param libelle - le libellé traduit par la barre
 */
export function entreeMenuSurveillance(
    libelle: string
): EntreeMenuSurveillance | null {
    const auth = useAuthStore()
    const ouverture = useOuvertureStore()
    // Ouvert pour CETTE session (personne et entreprise), pas pour la précédente.
    const ouvertIci = ouverture.ouvert && ouverture.luPourLaSession
    if (!ouvertIci || !auth.hasOfferEnergyManager) return null
    return {
        label: libelle,
        icon: "pi pi-bell",
        route: { path: CHEMIN_SURVEILLANCES },
    }
}

// ------------------------------------------------- lot F3 : l'équipement

/** Le segment des pages du module sous la fiche d'un équipement. */
export const SEGMENT_SURVEILLANCE = "surveillance"

/** Une entrée de la barre latérale d'un équipement. */
export interface EntreeEquipementSurveillance {
    key: string
    label: string
    icon: string
    route: { path: string }
}

/**
 * L'entrée « Surveillance » de la barre latérale d'une source ou d'un
 * bâtiment, juste après « Alarmes » de la V1, ou `null`. Même règle que la
 * barre du haut : ouvert pour CETTE session et offre Energy Manager. Jamais
 * désactivée (un lecteur voit l'état et acquitte). Sans store de session
 * actif (un composant monté hors application), elle n'existe pas : fermée
 * par défaut, comme le module.
 * @param libelle - le libellé traduit par la barre
 * @param base - le chemin de la fiche (`/source/<id>` ou `/building/<id>`)
 */
export function entreeSurveillanceEquipement(
    libelle: string,
    base: string
): EntreeEquipementSurveillance | null {
    if (!getActivePinia()) return null
    const entree = entreeMenuSurveillance(libelle)
    if (!entree) return null
    return {
        key: "surveillance",
        label: libelle,
        icon: entree.icon,
        route: { path: `${base}/${SEGMENT_SURVEILLANCE}` },
    }
}

/**
 * Le chemin de la fiche d'une surveillance, sous la fiche de son équipement,
 * ou `null` quand l'équipement n'est pas servi.
 * @param s - la surveillance
 */
export function cheminDeLaFiche(s: AlarmesV2Surveillance): string | null {
    const type = s.objet?.type
    const id = s.objet?._id
    if ((type !== "source" && type !== "building") || !id || !s._id) return null
    return `/${type}/${encodeURIComponent(id)}/${SEGMENT_SURVEILLANCE}/${encodeURIComponent(s._id)}`
}
